import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { MessageCircleX } from "lucide-react";
import ChatSession from "../components/ChatSession";
import { Message } from "../lib/chat-engine";
import { seedWidgetSession } from "../lib/widget-session";
import { identifyVisitor, saveContactId } from "../utils/visitor-identification";
import { UserInfoFormValues } from "../components/UserInfoForm";

// Standalone full-page chat, opened from a WhatsApp template URL button:
//   https://react-chatwidget-flowpat.vercel.app/c/<link-token>
//
// The token (minted by POST api/chat-link for one contact) is exchanged for a
// merged-channel widget session, the widget's identity storage is seeded, and
// the SAME ChatSession component the embedded widget uses mounts full-page —
// so the visitor lands INSIDE their own conversation: no phone-number form,
// with their WhatsApp and web messages interleaved chronologically, and the
// org's agent answering with full cross-channel context.
//
// DESIGN: deliberately chrome-free (Matt, 19 Aug 2026: "i do not want any
// header... super super clean"). The page is JUST the conversation, plus one
// tiny caption under the composer carrying the agent name and the way back to
// WhatsApp. Nothing else.
//
// MOBILE IS THE PRIMARY SURFACE (the link is tapped inside WhatsApp), so this
// page carries its own mobile hardening — the embedded widget does all of this
// through ChatWidget, which is not mounted here:
//   • iOS focus-zoom is killed twice over: viewport meta maximum-scale=1 AND a
//     16px floor on every input/textarea (iOS auto-zooms any focused control
//     under 16px — the shared ChatSession textarea is 14px).
//   • The page pins itself to the VISUAL viewport while the keyboard is open
//     (same technique as ChatWidget's mobile sync) so the composer is never
//     hidden and a stranded Safari pan can't wedge the layout.
//   • body scroll-lock + overscroll containment: the page never rubber-bands.
//   • Safe-area padding under the caption (viewport-fit=cover).

const WIDGET_API_BASE = "https://react-chatwidget-flowpat.vercel.app";

interface LinkExchange {
  organizationId: string;
  contactId: string;
  widgetId: string;
  sessionToken: string;
  sessionExp: number;
  contact: { fullName: string; phone: string; email: string };
  whatsapp: { number?: string; prefill_text?: string } | null;
}

interface WidgetConfig {
  agentName?: string | null;
  headerText?: string | null;
  customIcons?: { botWeb?: { url: string }; humanWeb?: { url: string } };
  customColors?: { buttonBackgroundColor?: string; buttonTextColor?: string };
  whatsapp?: { number?: string; prefill_text?: string } | null;
  shopifyWebsite?: string | null;
  storefront?: boolean;
}

type PageState = "loading" | "ready" | "error";

// Page-scoped hardening. A <style> tag (not classes) because two of these must
// reach into the SHARED ChatSession markup without forking it: the 16px input
// floor (the iOS focus-zoom killer) and the overscroll lock on html/body.
const PAGE_CSS = `
  html, body { overscroll-behavior: none; background: #ffffff; }
  .cl-page textarea, .cl-page input, .cl-page select {
    font-size: 16px !important;
  }
`;

const ChatLink = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>("loading");
  const [link, setLink] = useState<LinkExchange | null>(null);
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setState("error");
        return;
      }
      try {
        const res = await fetch(
          `${WIDGET_API_BASE}/api/chat-link?token=${encodeURIComponent(token)}`,
        );
        if (!res.ok) {
          if (!cancelled) setState("error");
          return;
        }
        const data: LinkExchange = await res.json();

        // Seed the exact storage the widget stack already reads — contact id
        // first so identifyVisitor adopts it as the visitorId, then the user
        // info ChatSession renders/sends with, then the merged-channel session
        // (with the link token alongside, so an expired session can renew
        // itself without losing WhatsApp history access).
        saveContactId(data.organizationId, data.contactId);
        try {
          localStorage.setItem(
            `flowapt_user_${data.organizationId}`,
            JSON.stringify({
              fullName: data.contact.fullName || "",
              email: data.contact.email || "",
              phone: data.contact.phone || "",
            }),
          );
        } catch {
          /* storage unavailable — ChatSession still gets userInfo via props */
        }
        seedWidgetSession(
          data.organizationId,
          data.contactId,
          data.sessionToken,
          data.sessionExp,
          token,
        );

        // Widget appearance/config — the same API the embedded widget uses.
        let cfg: WidgetConfig = {};
        try {
          const cfgRes = await fetch(
            `${WIDGET_API_BASE}/api/config-chat-widget?organizationId=${encodeURIComponent(
              data.organizationId,
            )}&widgetId=${encodeURIComponent(data.widgetId)}`,
          );
          if (cfgRes.ok) cfg = await cfgRes.json();
        } catch {
          /* config is cosmetic — the chat still works on defaults */
        }

        if (cancelled) return;
        setLink(data);
        setConfig(cfg);
        setState("ready");
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const agentName = config?.agentName || "AI Agent";

  useEffect(() => {
    // Loading/error included — the static index.html title is "FlowIQ Demo",
    // which must never show on the customer-facing chat host.
    document.title = state === "ready" ? `Chat with ${agentName}` : "openmy.chat";
  }, [state, agentName]);

  // ── iOS focus-zoom kill ───────────────────────────────────────────────────
  // maximum-scale=1 stops Safari's automatic zoom-to-input (iOS ≥10 still
  // allows accessibility pinch — this only disables the auto-zoom). The 16px
  // input floor in PAGE_CSS covers engines that ignore the viewport hint.
  useEffect(() => {
    const head = document.head;
    let viewport = document.querySelector('meta[name="viewport"]');
    const prevViewport = viewport?.getAttribute("content") ?? null;
    if (!viewport) {
      viewport = document.createElement("meta");
      viewport.setAttribute("name", "viewport");
      head.appendChild(viewport);
    }
    viewport.setAttribute(
      "content",
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover",
    );

    let theme = document.querySelector('meta[name="theme-color"]');
    const prevTheme = theme?.getAttribute("content") ?? null;
    const createdTheme = !theme;
    if (!theme) {
      theme = document.createElement("meta");
      theme.setAttribute("name", "theme-color");
      head.appendChild(theme);
    }
    theme.setAttribute("content", "#ffffff");

    return () => {
      if (prevViewport) viewport?.setAttribute("content", prevViewport);
      if (createdTheme) theme?.remove();
      else if (prevTheme) theme?.setAttribute("content", prevTheme);
    };
  }, []);

  // ── Keyboard-proof layout (mobile) ────────────────────────────────────────
  // Freeze the page for real (body position:fixed — iOS half-ignores
  // overflow:hidden) and pin the card to the VISUAL viewport whenever it
  // deviates from the layout viewport: keyboard open, mid-animation, or a
  // stranded Safari pan. Same technique the embedded widget uses on mobile.
  useEffect(() => {
    if (state !== "ready") return;
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 639px)").matches) return;

    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = "0";
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";

    const sync = () => {
      const el = cardRef.current;
      const vv = window.visualViewport;
      if (!el || !vv) return;
      const layoutH = document.documentElement.clientHeight;
      const deviates = vv.offsetTop > 2 || layoutH - vv.height > 2;
      if (deviates) {
        el.style.position = "fixed";
        el.style.top = `${vv.offsetTop}px`;
        el.style.left = "0";
        el.style.right = "0";
        el.style.height = `${vv.height}px`;
      } else {
        el.style.position = "";
        el.style.top = "";
        el.style.left = "";
        el.style.right = "";
        el.style.height = "";
      }
    };
    // Keyboard-close animations can settle without a final viewport event on
    // some iOS versions — re-sync shortly after any input loses focus.
    const onFocusOut = () => {
      setTimeout(sync, 350);
    };

    window.addEventListener("resize", sync);
    window.addEventListener("focusout", onFocusOut);
    window.visualViewport?.addEventListener("resize", sync);
    window.visualViewport?.addEventListener("scroll", sync);
    sync();

    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("focusout", onFocusOut);
      window.visualViewport?.removeEventListener("resize", sync);
      window.visualViewport?.removeEventListener("scroll", sync);
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
    };
  }, [state]);

  const visitorInfo = useMemo(
    () => (link ? identifyVisitor(link.organizationId) : null),
    [link],
  );

  const userInfo: UserInfoFormValues | null = useMemo(
    () =>
      link
        ? {
            fullName: link.contact.fullName || "Customer",
            email: link.contact.email || "",
            phone: link.contact.phone || "",
            countryCode: "",
            phoneNumber: link.contact.phone || "",
          }
        : null,
    [link],
  );

  const customIcons = useMemo(() => {
    if (!config?.customIcons) return undefined;
    return {
      botWeb: config.customIcons.botWeb ? (
        <img src={config.customIcons.botWeb.url} alt="Bot" width={16} height={16} />
      ) : undefined,
      humanWeb: config.customIcons.humanWeb ? (
        <img src={config.customIcons.humanWeb.url} alt="Support" width={16} height={16} />
      ) : undefined,
    };
  }, [config]);

  // The tiny caption's way back to the very thread the link arrived in.
  const waNumber = (
    link?.whatsapp?.number ||
    config?.whatsapp?.number ||
    ""
  ).replace(/[^\d]/g, "");

  if (state === "error") {
    return (
      <div className="h-[100dvh] w-full bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-sm w-full text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
            <MessageCircleX size={22} className="text-gray-500" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900">
            This chat link isn't valid anymore
          </h1>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">
            The link may have expired. Send us a message on WhatsApp and we'll
            reply there — or send you a fresh link.
          </p>
        </div>
      </div>
    );
  }

  if (state === "loading" || !link || !visitorInfo) {
    return (
      <div className="h-[100dvh] w-full bg-white flex items-center justify-center">
        <svg className="h-8 w-8 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Loading">
          <circle cx="12" cy="12" r="10" stroke="#E5E7EB" strokeWidth="2.5" />
          <path
            d="M22 12a10 10 0 0 1-10 10"
            stroke="#111827"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }

  return (
    <div className="cl-page h-[100dvh] w-full flex justify-center bg-white sm:bg-[#f4f5f7] sm:items-center sm:py-6">
      <style>{PAGE_CSS}</style>
      <div
        ref={cardRef}
        className="w-full max-w-xl h-full sm:h-[min(860px,calc(100dvh-3rem))] bg-white flex flex-col overflow-hidden sm:rounded-3xl sm:shadow-[0_24px_70px_-24px_rgba(15,23,42,0.35)] sm:ring-1 sm:ring-black/5"
      >
        {/* Just the conversation — no header, no chrome. */}
        <div className="flex-1 min-h-0 relative bg-white">
          <ChatSession
            organizationId={link.organizationId}
            widgetId={link.widgetId}
            visitorInfo={visitorInfo}
            contactId={link.contactId}
            userInfo={userInfo}
            messages={messages}
            setMessages={setMessages}
            onBack={() => {}}
            hideBackButton
            onSaveContactId={(id) => saveContactId(link.organizationId, id)}
            theme="light"
            customIcons={customIcons}
            customColors={{
              buttonBackgroundColor:
                config?.customColors?.buttonBackgroundColor || "#000000",
              buttonTextColor: config?.customColors?.buttonTextColor || "#FFFFFF",
            }}
            agentName={config?.agentName || undefined}
            twoFa={false}
            shopifyWebsite={config?.shopifyWebsite || null}
            storefront={config?.storefront === true}
          />
        </div>

        {/* Tiny caption under the composer — the page's ONLY chrome. */}
        <div
          className="flex-shrink-0 bg-white text-center px-4 pb-1.5"
          style={{ paddingBottom: "max(0.375rem, env(safe-area-inset-bottom))" }}
        >
          <p className="text-[10px] leading-4 text-gray-400 truncate">
            {agentName} · your WhatsApp chat, continued here
            {waNumber && (
              <>
                {" · "}
                <a
                  href={`https://wa.me/${waNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 decoration-gray-300 hover:text-gray-600"
                >
                  back to WhatsApp
                </a>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatLink;
